/**
 * Shared palettes for the armor-type icon variants (cloth / leather / plate).
 * The plain helm/armor/gloves/boots/belt/shoulders/bracers/legs icons are mail;
 * these swap the silhouette and base material while still picking up the tier
 * accents. Also the bracer and legs geometry every armor type shares, so
 * those slots keep one recognisable silhouette across the types.
 */
import { PAL } from '../../palette';
import type { Col, Grid } from '../env/raster';
import type { Material } from './materials';

export type Ramp4 = readonly [Col, Col, Col, Col];
export type Ramp3 = readonly [Col, Col, Col];

/** Mage fabric per tier: undyed linen, grey wool, royal blue, white silk, crimson, violet. */
const FABRIC: readonly Ramp4[] = [
  [PAL.darkBrown, PAL.brown, PAL.tan, PAL.sand],
  [PAL.navy, PAL.darkSlate, PAL.slate, PAL.gray],
  [PAL.navy, PAL.blue, PAL.sky, '#7fd3f5'],
  [PAL.slate, PAL.gray, PAL.lightGray, PAL.white],
  [PAL.plum, PAL.darkRed, PAL.red, PAL.pink],
  ['#2a1238', PAL.purple, PAL.magenta, PAL.pink],
];

/** Embroidered trim on the fabric (dark → light). */
const FABRIC_TRIM: readonly Ramp3[] = [
  [PAL.plum, PAL.darkBrown, '#8f5a44'],
  [PAL.slate, PAL.gray, PAL.lightGray],
  [PAL.rust, PAL.gold, PAL.yellow],
  [PAL.blue, PAL.sky, PAL.cyan],
  [PAL.rust, PAL.gold, PAL.yellow],
  [PAL.rust, PAL.gold, PAL.yellow],
];

/** Leather per tier: worn hide, tanned, oiled, blue-dyed, ember-red, violet. */
const LEATHER: readonly Ramp4[] = [
  [PAL.plum, PAL.darkBrown, '#8f5a44', PAL.brown],
  [PAL.plum, PAL.darkBrown, PAL.brown, PAL.tan],
  ['#2a1a22', PAL.plum, PAL.darkBrown, PAL.brown],
  [PAL.navy, PAL.darkSlate, PAL.blue, PAL.sky],
  [PAL.plum, PAL.darkRed, PAL.rust, PAL.orangeBrown],
  ['#1c0f2a', PAL.purple, PAL.magenta, PAL.pink],
];

/** Fittings on leather (buckles, studs): dull iron, iron, then the tier trim. */
const FITTINGS: readonly Ramp3[] = [
  [PAL.darkSlate, PAL.slate, PAL.gray],
  [PAL.slate, PAL.gray, PAL.lightGray],
];

const clampTier = (t: number) => Math.max(0, Math.min(5, Math.floor(Number.isFinite(t) ? t : 0)));

export function fabric(tier: number): Ramp4 {
  return FABRIC[clampTier(tier)];
}

export function fabricTrim(tier: number): Ramp3 {
  return FABRIC_TRIM[clampTier(tier)];
}

export function leather(tier: number): Ramp4 {
  return LEATHER[clampTier(tier)];
}

export function fittings(m: Material, tier: number): Ramp3 {
  const t = clampTier(tier);
  return t < FITTINGS.length ? FITTINGS[t] : m.trim;
}

/** Accent colour for runes, gems and feathers; glowing tiers use their glow. */
export function accent(m: Material): Col {
  return m.glow ?? m.gem;
}

// ------------------------------------------------------------- bracers ----

/** Where a pixel sits on a bracer. */
export interface TubeSpot {
  /** 0 at the wrist → 1 at the elbow */
  u: number;
  /** across the tube: -1 lit upper-left edge … 1 shaded lower-right edge */
  v: number;
  /** distance along the tube from the wrist, in pixels */
  a: number;
  /** outside of the guard, the lip of the elbow opening, or the dark inside */
  part: 'body' | 'lip' | 'hole';
}

export interface TubeSpec {
  /** wrist (bottom-left) and elbow (top-right) centres */
  x0: number;
  y0: number;
  x1: number;
  y1: number;
  /** half thickness along the guard (u: 0 wrist → 1 elbow) */
  r: (u: number) => number;
}

/** The default forearm guard: a flared cuff at the wrist, widening toward the elbow. */
export const FOREARM: TubeSpec = {
  x0: 3.3,
  y0: 12.7,
  x1: 11.3,
  y1: 4.7,
  r: (u) => (u < 0.12 ? 2.8 : 2.3 + u * 1.1),
};

/**
 * Rasterise a bracer: a guard lying on the diagonal from a square-cut wrist
 * (bottom-left) to the elbow (top-right), open there so its dark inside shows.
 * Every armor type shares this silhouette so the slot reads the same.
 */
export function tube(
  g: Grid,
  spec: TubeSpec,
  paint: (x: number, y: number, s: TubeSpot) => Col | null | undefined,
): void {
  const dx = spec.x1 - spec.x0;
  const dy = spec.y1 - spec.y0;
  const len = Math.hypot(dx, dy);
  const ax = dx / len;
  const ay = dy / len;
  const r1 = spec.r(1);
  // the opening, an ellipse seen at an angle
  const depth = Math.max(1.3, r1 * 0.42);
  for (let y = 0; y < g.h; y++)
    for (let x = 0; x < g.w; x++) {
      const px = x + 0.5 - spec.x0;
      const py = y + 0.5 - spec.y0;
      const a = px * ax + py * ay;
      // across points to the lower right (the shaded side)
      const b = py * ax - px * ay;
      if (((a - len) / depth) ** 2 + (b / r1) ** 2 <= 1) {
        const hole = ((a - len + 0.3) / (depth - 0.7)) ** 2 + (b / (r1 - 1)) ** 2 <= 1;
        g.set(x, y, paint(x, y, { u: 1, v: b / r1, a, part: hole ? 'hole' : 'lip' }));
        continue;
      }
      if (a < 0 || a > len) continue;
      const u = a / len;
      const r = spec.r(u);
      if (Math.abs(b) <= r) g.set(x, y, paint(x, y, { u, v: b / r, a, part: 'body' }));
    }
}

/** The pixel at `u` along a bracer and `v` across it (|v| > 1 lands just outside). */
export function tubeAt(spec: TubeSpec, u: number, v: number): [number, number] {
  const dx = spec.x1 - spec.x0;
  const dy = spec.y1 - spec.y0;
  const len = Math.hypot(dx, dy);
  const a = u * len;
  const b = v * spec.r(u);
  return [Math.floor(spec.x0 + (a * dx - b * dy) / len), Math.floor(spec.y0 + (a * dy + b * dx) / len)];
}

// ---------------------------------------------------------------- legs ----

/** Where a pixel sits on a pair of legs. */
export interface LegSpot {
  /** 0 = the near (left, lit) leg, 1 = the far (right, shaded) one; on the hips, the nearer leg */
  leg: 0 | 1;
  /** across the leg: -1 outer edge … 1 inner edge */
  n: number;
  /** 0 at the crotch (and on the hips) → 1 at the hem */
  t: number;
}

export interface LegsSpec {
  /** first row of the hips, the crotch row and the hem row */
  top: number;
  crotch: number;
  hem: number;
  /** half width of one leg from the crotch (t = 0) to the hem (t = 1) */
  half: (t: number) => number;
  /** how far each leg drifts outward by the hem */
  spread: number;
}

/**
 * Rasterise leg armor seen from the front: hips across the top, then two legs
 * from the crotch down to the hem with a gap opening between them. No feet, so
 * it never reads as boots; the waist band is drawn by the caller above `top`.
 */
export function legsShape(
  g: Grid,
  spec: LegsSpec,
  paint: (x: number, y: number, s: LegSpot) => Col | null | undefined,
): void {
  const h0 = spec.half(0);
  for (let y = spec.top; y <= spec.hem; y++) {
    const t = y < spec.crotch ? 0 : (y - spec.crotch + 1) / (spec.hem - spec.crotch + 1);
    const half = spec.half(t);
    for (const leg of [0, 1] as const) {
      const side = leg ? 1 : -1;
      const cx = 8 + side * (h0 + spec.spread * t);
      for (let x = 0; x < g.w; x++) {
        const d = (x + 0.5 - cx) * side;
        // on the hips the legs meet in the middle
        const inner = y < spec.crotch ? side * (x + 0.5 - 8) >= 0 : d >= -half;
        if (!inner || d > half) continue;
        g.set(x, y, paint(x, y, { leg, n: Math.max(-1, Math.min(1, -d / half)), t }));
      }
    }
  }
}
